import React, { useState } from 'react';
import { Form, Button, Image, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';

const ReplyInput = ({
    postId,
    parentReplyId = null,
    parentUserName = null,
    userProfilePic,
    onSubmit,
    onCancel,
    onContentChange,
    placeholder = 'Write a reply...',
}) => {
    const [content, setContent] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!content.trim()) {
            return;
        }

        setIsSubmitting(true);
        setError(null);
        try {
            await onSubmit({
                postId,
                parentId: parentReplyId,
                content: content.trim(),
            });
            setContent('');
            if (onContentChange) {
                onContentChange('');
            }
            // Don't call onCancel() here - parent component handles closing the reply box
            // Calling onCancel() here causes the draft warning to appear incorrectly
        } catch (err) {
            console.error('Error submitting reply:', err);
            setError(err.message || 'Failed to post reply. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setContent('');
        setError(null);
        if (onContentChange) {
            onContentChange('');
        }
        if (onCancel) {
            onCancel();
        }
    };

    return (
        <div className="reply-input-container mb-3">
            {parentUserName && (
                <div className="reply-input-header mb-2">
                    <small className="text-muted">
                        Replying to <strong>@{parentUserName}</strong>
                    </small>
                </div>
            )}
            {error && (
                <Alert
                    variant="danger"
                    className="mb-2"
                    dismissible
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}
            <Form onSubmit={handleSubmit}>
                <div className="reply-input-wrapper d-flex align-items-start gap-2">
                    {userProfilePic && (
                        <Image
                            src={userProfilePic}
                            alt="Your profile"
                            roundedCircle
                            className="reply-input-avatar"
                        />
                    )}
                    <div className="flex-grow-1">
                        <Form.Control
                            as="textarea"
                            rows={1}
                            value={content}
                            onChange={(e) => {
                                setContent(e.target.value);
                                if (!e.target.value) setError(null);
                                if (onContentChange) {
                                    onContentChange(e.target.value);
                                }
                            }}
                            placeholder={placeholder}
                            disabled={isSubmitting}
                            className="reply-input-textarea"
                            maxLength={10000}
                        />
                        <div className="d-flex justify-content-between align-items-center mt-1">
                            <Form.Text className="text-muted small">
                                {content.length} / 10,000
                            </Form.Text>
                            <div className="reply-input-actions d-flex gap-2">
                                {onCancel && (
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={handleCancel}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </Button>
                                )}
                                <Button
                                    type="submit"
                                    variant="primary"
                                    size="sm"
                                    disabled={!content.trim() || isSubmitting}
                                >
                                    {isSubmitting ? 'Posting...' : 'Reply'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Form>
        </div>
    );
};

ReplyInput.propTypes = {
    postId: PropTypes.string.isRequired,
    parentReplyId: PropTypes.string,
    parentUserName: PropTypes.string,
    userProfilePic: PropTypes.string,
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
    onContentChange: PropTypes.func,
    placeholder: PropTypes.string,
};

export default ReplyInput;
